import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Processing Bridge Hook
 * 
 * This hook automatically triggers the enhanced-process-bill edge function
 * when new items are uploaded to the queue and creates the queue-to-bills data flow.
 */
export const useProcessingBridge = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user?.id) return;

    // Set up real-time subscription to bill_upload_queue for new uploads
    const queueSubscription = supabase
      .channel('processing-bridge-queue')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'bill_upload_queue',
          filter: `user_id=eq.${user.id}`
        },
        async (payload) => {
          const queueItem = payload.new;
          console.log('New queue item detected:', queueItem.id);

      // Auto-trigger processing for newly uploaded items only when intake criteria met
      if (
        queueItem.upload_status === 'uploaded' &&
        queueItem.processing_status === 'pending' &&
        (queueItem.is_readable ?? true) &&
        !queueItem.is_duplicate &&
        !!queueItem.assigned_to_account_id
      ) {
        try {
          console.log('Auto-triggering processing for queue item:', queueItem.id);
              
              const { data, error } = await supabase.functions.invoke('multi-tier-bill-parser', {
                body: { queue_item_id: queueItem.id }
              });

              if (error) {
                console.error('Failed to auto-trigger processing:', error);
                toast({
                  title: "Processing Error",
                  description: `Failed to auto-process ${queueItem.file_name}: ${error.message}`,
                  variant: "destructive",
                });
              } else {
                console.log('Processing triggered successfully for:', queueItem.id);
                toast({
                  title: "Processing Started",
                  description: `Auto-processing started for ${queueItem.file_name}`,
                });
              }
            } catch (error: any) {
              console.error('Error triggering processing:', error);
              toast({
                title: "Processing Error",
                description: `Failed to auto-process ${queueItem.file_name}`,
                variant: "destructive",
              });
            }
          }
        }
      )
      .subscribe();

    // Set up real-time subscription for queue processing completion
    const processedSubscription = supabase
      .channel('processing-bridge-completed')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'bill_upload_queue',
          filter: `user_id=eq.${user.id}`
        },
        async (payload) => {
          const queueItem = payload.new;
          
          // Check if item completed processing and has an account assigned (removed auto_assigned requirement)
          if (
            queueItem.processing_status === 'completed' && 
            queueItem.assigned_to_account_id
          ) {
            console.log('Completed queue item with account detected, creating bill:', queueItem.id);
            
            try {
              await createBillFromQueueItem(queueItem);
              
              toast({
                title: "Bill Created",
                description: `Successfully created bill from ${queueItem.file_name}`,
              });

              // Invalidate relevant queries
              queryClient.invalidateQueries({ queryKey: ['bills'] });
              queryClient.invalidateQueries({ queryKey: ['bill-stats'] });
              queryClient.invalidateQueries({ queryKey: ['bill-queue'] });
              
            } catch (error: any) {
              console.error('Failed to create bill from queue item:', error);
              toast({
                title: "Bill Creation Error",
                description: `Failed to create bill from ${queueItem.file_name}: ${error.message}`,
                variant: "destructive",
              });
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(queueSubscription);
      supabase.removeChannel(processedSubscription);
    };
  }, [user?.id, toast, queryClient]);

  /**
   * Creates a bill record from a processed queue item with enhanced validation and error recovery
   */
  const createBillFromQueueItem = async (queueItem: any) => {
    try {
      // Enhanced validation
      const validationErrors = [];
      
      if (!queueItem.extracted_data) {
        validationErrors.push('Missing extracted data');
      }
      
      if (!queueItem.assigned_to_account_id) {
        validationErrors.push('Missing account assignment');
      }

      if (validationErrors.length > 0) {
        throw new Error(`Queue item validation failed: ${validationErrors.join(', ')}`);
      }

      const extractedData = queueItem.extracted_data;
      
      // Enhanced data transformation with fallbacks and validation
      const transformData = () => {
        const now = new Date().toISOString();
        
        // Validate and transform amounts
        const amount = extractedData.amount || queueItem.amount;
        const usageAmount = extractedData.usage_amount;
        
        if (amount && (isNaN(amount) || amount < 0)) {
          console.warn('Invalid amount detected, setting to null:', amount);
        }

        // Date validation and transformation
        const billDate = extractedData.bill_date || queueItem.bill_date;
        const dueDate = extractedData.due_date;
        
        return {
          user_id: queueItem.user_id,
          account_id: queueItem.assigned_to_account_id,
          bill_date: billDate || new Date().toISOString().split('T')[0],
          due_date: dueDate,
          amount: amount && !isNaN(amount) && amount >= 0 ? amount : 0,
          currency: extractedData.currency || 'USD',
          usage_amount: usageAmount && !isNaN(usageAmount) && usageAmount >= 0 ? usageAmount : null,
          usage_unit: extractedData.usage_unit || 'kWh',
          billing_period_start: extractedData.billing_period_start,
          billing_period_end: extractedData.billing_period_end,
          bill_number: extractedData.bill_number || queueItem.bill_number,
          pdf_url: queueItem.file_path,
          file_name: queueItem.file_name,
          mime_type: queueItem.mime_type,
          file_size: queueItem.file_size,
          raw_data: queueItem.extracted_data,
          parsed_data: extractedData,
          processing_status: 'completed',
          ai_confidence: Math.min(Math.max(queueItem.ai_confidence || 0, 0), 1),
          supplier_confidence: Math.min(Math.max(queueItem.customer_confidence || 0, 0), 1),
          customer_confidence: Math.min(Math.max(queueItem.customer_confidence || 0, 0), 1),
          account_confidence: Math.min(Math.max(queueItem.account_confidence || 0, 0), 1),
          was_auto_assigned: queueItem.auto_assigned || false,
          matching_details: queueItem.matching_details || {},
          collection_status: 'collected',
          supplier_name: extractedData.customer_name || queueItem.supplier_name || 'Unknown Supplier',
          bill_type: extractedData.service_type || queueItem.service_type || 'utility',
          matching_category: queueItem.matching_category,
          account_number_raw: queueItem.account_number_raw,
          account_number_normalized: queueItem.account_number_normalized,
          customer_name_raw: queueItem.customer_name_raw,
          customer_name_normalized: queueItem.customer_name_normalized,
          processing_started_at: queueItem.processing_started_at || now,
          processing_completed_at: queueItem.processing_completed_at || now,
          validation_status: queueItem.validation_errors?.length > 0 ? 'failed' : 'passed',
          notes: queueItem.notes,
          tags: extractedData.tags || []
        };
      };

      const billData = transformData();

      // Log transformation for debugging
      console.log('Transforming queue item to bill:', {
        queueItemId: queueItem.id,
        accountId: queueItem.assigned_to_account_id,
        amount: billData.amount,
        confidence: billData.ai_confidence
      });

      // Insert the bill with retry logic
      const insertWithRetry = async (data: any, retries = 3) => {
        for (let attempt = 1; attempt <= retries; attempt++) {
          try {
            const { data: newBill, error } = await supabase
              .from('bills')
              .insert([data])
              .select()
              .single();

            if (error) {
              throw error;
            }

            return newBill;
          } catch (error: any) {
            console.error(`Insert attempt ${attempt} failed:`, error);
            
            if (attempt === retries) {
              throw new Error(`Failed to insert bill after ${retries} attempts: ${error.message}`);
            }
            
            // Wait before retry
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          }
        }
      };

      const newBill = await insertWithRetry(billData);
      
      console.log('Successfully created bill from queue item:', {
        billId: newBill.id,
        queueItemId: queueItem.id,
        amount: newBill.amount,
        confidence: newBill.ai_confidence
      });

      // Update queue item with success status
      await supabase
        .from('bill_upload_queue')
        .update({ 
          notes: `Successfully converted to bill ${newBill.id} at ${new Date().toISOString()}`,
          assignment_status: queueItem.auto_assigned ? 'auto_assigned' : 'manually_assigned'
        })
        .eq('id', queueItem.id);

      // Create audit log entry
      await supabase
        .from('analytics_events')
        .insert({
          user_id: queueItem.user_id,
          event_type: 'bill_created_from_queue',
          event_data: {
            bill_id: newBill.id,
            queue_item_id: queueItem.id,
            was_auto_assigned: queueItem.auto_assigned,
            processing_time_ms: queueItem.processing_completed_at && queueItem.processing_started_at 
              ? new Date(queueItem.processing_completed_at).getTime() - new Date(queueItem.processing_started_at).getTime()
              : null,
            confidence_scores: {
              ai: billData.ai_confidence,
              account: billData.account_confidence,
              customer: billData.customer_confidence
            }
          }
        });

      return newBill;
      
    } catch (error: any) {
      console.error('Error creating bill from queue item:', error);
      
      // Update queue item with error status
      await supabase
        .from('bill_upload_queue')
        .update({ 
          notes: `Failed to convert to bill: ${error.message}`,
          processing_error: error.message
        })
        .eq('id', queueItem.id);

      // Log error event
      await supabase
        .from('analytics_events')
        .insert({
          user_id: queueItem.user_id,
          event_type: 'bill_creation_failed',
          event_data: {
            queue_item_id: queueItem.id,
            error_message: error.message,
            timestamp: new Date().toISOString()
          }
        });

      throw error;
    }
  };

  /**
   * Manually process a queue item
   */
  const processQueueItem = async (queueItemId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('multi-tier-bill-parser', {
        body: { queue_item_id: queueItemId }
      });

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: "Processing Started",
        description: "Bill processing initiated successfully",
      });

      return data;
    } catch (error: any) {
      toast({
        title: "Processing Failed",
        description: error.message || "Failed to start processing",
        variant: "destructive",
      });
      throw error;
    }
  };

  /**
   * Manually create bill from completed queue item
   */
  const createBillFromQueue = async (queueItemId: string, accountId: string) => {
    try {
      // Get the queue item
      const { data: queueItem, error } = await supabase
        .from('bill_upload_queue')
        .select('*')
        .eq('id', queueItemId)
        .single();

      if (error || !queueItem) {
        throw new Error('Queue item not found');
      }

      // Update with assignment info
      const updatedQueueItem = {
        ...queueItem,
        assigned_to_account_id: accountId,
        auto_assigned: false,
        assigned_at: new Date().toISOString(),
        assigned_by: user?.id
      };

      await createBillFromQueueItem(updatedQueueItem);

      // Update the queue item status
      await supabase
        .from('bill_upload_queue')
        .update({
          assigned_to_account_id: accountId,
          assignment_status: 'manually_assigned',
          assigned_at: new Date().toISOString(),
          assigned_by: user?.id
        })
        .eq('id', queueItemId);

      toast({
        title: "Bill Created",
        description: "Successfully created bill from queue item",
      });

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      queryClient.invalidateQueries({ queryKey: ['bill-queue'] });

    } catch (error: any) {
      toast({
        title: "Creation Failed",
        description: error.message || "Failed to create bill",
        variant: "destructive",
      });
      throw error;
    }
  };

  return {
    processQueueItem,
    createBillFromQueue,
    createBillFromQueueItem
  };
};